<template>
  <div class="kanban-board-container flex flex-col h-full bg-slate-50 dark:bg-slate-900 w-full relative">
    <div class="kanban-header flex flex-col gap-3 p-4 border-b border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800 shadow-sm">
      <div class="flex items-center justify-between">
        <h1 class="text-xl font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <i class="i-lucide-trello text-primary-500"></i> {{ kanbanAttribute ? kanbanAttribute.attributeDisplayName : 'Kanban' }}
          <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <span class="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Tempo Real ({{ totalCardsCount }} cartões)
          </span>
        </h1>
        <div class="kanban-actions flex items-center gap-2">
          <button @click="loadConversations" class="btn btn-sm btn-outline button flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-semibold">
            <i class="i-lucide-refresh-cw" :class="{ 'animate-spin': loading }"></i> Atualizar
          </button>
        </div>
      </div>
      
      <!-- Filtros -->
      <div class="flex items-center gap-3">
        <div class="flex-1 max-w-sm">
          <input type="text" v-model="searchQuery" placeholder="Pesquisar por cliente, telefone ou ID..." class="w-full text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-1.5 focus:border-primary-500 focus:ring-1 focus:primary-500 outline-none" />
        </div>
        <div class="flex-1 max-w-sm relative" v-if="allLabels.length > 0">
          <select v-model="selectedLabelFilter" class="w-full text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-1.5 focus:border-primary-500 focus:ring-1 focus:primary-500 outline-none">
            <option value="">Todas as etiquetas</option>
            <option v-for="label in allLabels" :key="label.id" :value="label.title">{{ label.title }}</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="loading && conversations.length === 0" class="flex items-center justify-center h-full">
      <div class="flex flex-col items-center gap-3">
        <div class="spinner border-4 border-slate-200 dark:border-slate-700 border-t-primary-500 rounded-full w-8 h-8 animate-spin"></div>
        <span class="text-sm font-medium text-slate-500 dark:text-slate-400">Carregando cartões do funil...</span>
      </div>
    </div>

    <div v-else class="kanban-columns-wrapper flex-1 overflow-x-auto p-4 flex gap-4 h-full items-start">
      <div v-for="column in columns" :key="column" class="kanban-column bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700/50 w-80 min-w-[320px] flex flex-col max-h-full">
        <div class="column-header p-3 border-b border-slate-200 dark:border-slate-700/50 flex items-center justify-between bg-white dark:bg-slate-800 rounded-t-lg sticky top-0 shadow-xs" :style="{ borderTop: '4px solid ' + getColumnColor(column) }">
          <div class="flex items-center gap-2">
            <span class="font-semibold text-slate-700 dark:text-slate-200">{{ column }}</span>
            <span class="badge bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full text-xs font-bold">{{ (columnsMap[column] || []).length }}</span>
          </div>
          <div>
            <input type="color" :value="getColumnColor(column)" @change="e => saveColumnColor(column, e.target.value)" class="w-5 h-5 p-0 border-0 rounded cursor-pointer bg-transparent" title="Personalizar cor" />
          </div>
        </div>
        
        <div class="column-body p-2 overflow-y-auto flex-1 min-h-[150px] max-h-[calc(100vh-210px)]">
          <draggable
            :list="columnsMap[column] || []"
            group="kanban"
            itemKey="display_id"
            @change="evt => onCardChange(evt, column)"
            class="min-h-[100px] flex flex-col gap-2.5"
          >
            <template #item="{ element }">
              <kanban-card :chat="element" @click="openConversation(element)" />
            </template>
          </draggable>
        </div>
      </div>
    </div>
    
    <!-- Modal de Conversa Instantâneo -->
    <woot-modal v-model:show="showConversationModal" :on-close="closeConversationModal" size="kanban-modal" :show-close-button="false">
      <div class="h-[90vh] flex flex-col w-full overflow-hidden bg-white dark:bg-slate-900 rounded-xl shadow-2xl" v-if="showConversationModal && selectedConversation">
        
        <!-- Header da Barra Superior do Modal -->
        <div class="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700/80 shrink-0">
          <div class="flex items-center gap-2.5 min-w-0">
            <span class="px-2.5 py-0.5 rounded-md text-xs font-bold bg-primary-500/15 text-primary-600 dark:text-primary-400 border border-primary-500/30">
              Atendimento #{{ selectedConversation.display_id || selectedConversation.id }}
            </span>
            <h3 class="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
              {{ selectedConversation.meta?.sender?.name || 'Cliente' }}
            </h3>
            <span v-if="selectedConversation.meta?.sender?.phone_number" class="text-xs text-slate-500 dark:text-slate-400 font-medium">
              ({{ selectedConversation.meta?.sender?.phone_number }})
            </span>
          </div>

          <div class="flex items-center gap-2">
            <!-- Botão "Ir para a conversa" -->
            <button
              @click="navigateToFullConversation"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
              title="Abrir no módulo principal de conversas"
            >
              <i class="i-lucide-external-link text-xs"></i>
              <span>Ir para a conversa</span>
            </button>

            <!-- Botão "Contato" (Alterna o painel lateral do cliente) -->
            <button
              @click="toggleContactSidebar"
              :class="[
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer',
                isSidebarOpen
                  ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 border-primary-300 dark:border-primary-700'
                  : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600'
              ]"
              title="Abrir ou fechar detalhes do contato"
            >
              <i class="i-lucide-user text-xs"></i>
              <span>Contato</span>
            </button>

            <!-- Botão Fechar Modal -->
            <button
              @click="closeConversationModal"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 transition-all cursor-pointer"
              title="Fechar (Esc)"
            >
              <i class="i-lucide-x size-4"></i>
            </button>
          </div>
        </div>

        <!-- Corpo do Modal: Mensagens + Painel Lateral de Contato -->
        <div class="flex-1 flex flex-row w-full overflow-hidden min-h-0">
          <conversation-box :inbox-id="selectedConversation.inbox_id" :is-inbox-view="false" class="flex-1 min-w-0" />
          <conversation-sidebar v-if="isSidebarOpen && currentChat && currentChat.id" :current-chat="currentChat" />
        </div>

      </div>
    </woot-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from '../../../components/widgets/conversation/ConversationSidebar.vue';
import Modal from '../../../components/Modal.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    return { uiSettings, updateUISettings };
  },
  components: {
    draggable,
    KanbanCard,
    ConversationBox,
    ConversationSidebar,
    WootModal: Modal
  },
  data() {
    return {
      loading: true,
      conversations: [],
      columnsMap: {},
      searchQuery: '',
      selectedLabelFilter: '',
      showConversationModal: false,
      selectedConversation: null,
      unsubscribeStore: null,
    };
  },
  computed: {
    allLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
    accountId() {
      return this.$route.params.accountId;
    },
    attributeKey() {
      return this.$route.params.attributeKey || 'servidor';
    },
    currentChat() {
      return this.$store.getters.getSelectedChat;
    },
    isSidebarOpen() {
      if (!this.uiSettings) return true;
      return this.uiSettings.is_contact_sidebar_open !== false;
    },
    kanbanAttribute() {
      const attrs = this.$store.getters['attributes/getConversationAttributes'] || [];
      return attrs.find(a => a.attributeKey === this.attributeKey) || null;
    },
    columns() {
      if (this.kanbanAttribute && this.kanbanAttribute.attributeValues && this.kanbanAttribute.attributeValues.length > 0) {
        return this.kanbanAttribute.attributeValues;
      }
      return [
        'Recepção e Qualificação',
        'Holerite',
        'Proposta',
        'Autorizado',
        'Aguardando Assinatura',
        'Analise Viabiliadade',
        'Pasta Fechada',
        'Distribuido',
        'PRAZOS',
        'Prazos Doc Enviados',
        'Novo Contato',
        'Qualificação / Demonstração',
        'Fechamento',
        'Onboarding',
        'Suporte'
      ];
    },
    totalCardsCount() {
      return this.conversations.length;
    }
  },
  watch: {
    attributeKey() {
      this.loadConversations();
    },
    searchQuery() {
      this.updateColumnsMap();
    },
    selectedLabelFilter() {
      this.updateColumnsMap();
    }
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('campaigns/get');
    this.$store.dispatch('inboxes/get');

    window.addEventListener('kanban-close-modal', this.closeConversationModal);

    window.__kanban_cache = window.__kanban_cache || {};
    const cached = window.__kanban_cache[this.attributeKey];
    if (cached && cached.length > 0) {
      this.conversations = cached;
      this.updateColumnsMap();
      this.loading = false;
    }
    
    this.loadConversations();

    this.unsubscribeStore = this.$store.subscribe((mutation) => {
      const type = mutation.type || '';
      if (
        type.includes('UPDATE_CONVERSATION') ||
        type.includes('ADD_CONVERSATION') ||
        type.includes('SET_ALL_CONVERSATION_CUSTOM_ATTRIBUTES') ||
        type.includes('SET_CURRENT_CHAT_WINDOW')
      ) {
        this.handleRealtimeConversationUpdate(mutation.payload);
      }
    });
  },
  beforeUnmount() {
    this.cleanupRealtime();
  },
  methods: {
    cleanupRealtime() {
      window.removeEventListener('kanban-close-modal', this.closeConversationModal);
      if (this.unsubscribeStore) {
        this.unsubscribeStore();
        this.unsubscribeStore = null;
      }
    },
    getCustomAttributes(conv) {
      if (!conv || !conv.custom_attributes) return {};
      if (typeof conv.custom_attributes === 'string') {
        try {
          return JSON.parse(conv.custom_attributes);
        } catch (e) {
          return {};
        }
      }
      return conv.custom_attributes;
    },
    updateColumnsMap() {
      const map = {};
      this.columns.forEach(col => {
        map[col] = [];
      });
      
      const q = (this.searchQuery || '').toLowerCase().trim();
      const labelFilter = this.selectedLabelFilter;
      
      this.conversations.forEach(c => {
        const customAttrs = this.getCustomAttributes(c);
        const stage = customAttrs[this.attributeKey];
        if (!stage) return;
        
        const stageNorm = String(stage).toLowerCase().trim();
        const matchingCol = this.columns.find(col => String(col).toLowerCase().trim() === stageNorm);
        
        if (matchingCol) {
          if (q) {
            const nameMatch = (c.meta?.sender?.name || '').toLowerCase().includes(q);
            const phoneMatch = (c.meta?.sender?.phone_number || '').toLowerCase().includes(q);
            const idMatch = String(c.display_id || c.id).includes(q);
            if (!nameMatch && !phoneMatch && !idMatch) return;
          }
          if (labelFilter) {
            if (!(c.labels || []).includes(labelFilter)) return;
          }
          map[matchingCol].push(c);
        }
      });
      
      this.columnsMap = map;
    },
    handleRealtimeConversationUpdate(updatedConv) {
      if (!updatedConv || (!updatedConv.id && !updatedConv.display_id)) return;

      const convId = updatedConv.display_id || updatedConv.id;
      const currentAttrKey = this.attributeKey;
      const customAttrs = this.getCustomAttributes(updatedConv);
      const stage = customAttrs[currentAttrKey];
      const existingIndex = this.conversations.findIndex(c => (c.display_id || c.id) === convId);

      if (existingIndex !== -1) {
        if (stage) {
          const existing = this.conversations[existingIndex];
          const existingAttrs = this.getCustomAttributes(existing);
          this.conversations.splice(existingIndex, 1, {
            ...existing,
            ...updatedConv,
            display_id: convId,
            id: convId,
            custom_attributes: {
              ...existingAttrs,
              ...customAttrs,
            },
            meta: {
              ...(existing.meta || {}),
              ...(updatedConv.meta || {}),
            },
          });
        } else {
          this.conversations.splice(existingIndex, 1);
        }
      } else if (stage) {
        this.conversations.unshift({
          ...updatedConv,
          display_id: convId,
          id: convId,
          custom_attributes: customAttrs,
        });
      }

      this.updateColumnsMap();

      if (window.__kanban_cache) {
        window.__kanban_cache[this.attributeKey] = this.conversations;
      }
    },
    async loadConversations() {
      this.loading = true;
      try {
        const res = await window.axios.get(
          `/api/v1/accounts/${this.accountId}/kanban_boards/conversations?attribute_key=${this.attributeKey}`
        );
        if (res.data && res.data.conversations) {
          this.conversations = res.data.conversations;
          this.updateColumnsMap();
          window.__kanban_cache = window.__kanban_cache || {};
          window.__kanban_cache[this.attributeKey] = this.conversations;
          return;
        }
      } catch (e) {
        console.warn('Erro ao carregar kanban dedicado', e);
      } finally {
        this.loading = false;
      }
    },
    getColumnColor(column) {
      if (this.kanbanAttribute && this.kanbanAttribute.attributeDescription) {
        try {
          const config = JSON.parse(this.kanbanAttribute.attributeDescription);
          if (config && config.colors && config.colors[column]) {
            return config.colors[column];
          }
        } catch (e) {
          // ignore
        }
      }
      const defaultPalette = {
        'Recepção e Qualificação': '#3b82f6',
        'Holerite': '#06b6d4',
        'Proposta': '#f59e0b',
        'Autorizado': '#8b5cf6',
        'Aguardando Assinatura': '#ec4899',
        'Analise Viabiliadade': '#6366f1',
        'Pasta Fechada': '#64748b',
        'Distribuido': '#10b981',
        'PRAZOS': '#ef4444',
        'Prazos Doc Enviados': '#14b8a6',
        'Novo Contato': '#3b82f6',
        'Qualificação / Demonstração': '#06b6d4',
        'Fechamento': '#10b981',
        'Onboarding': '#8b5cf6',
        'Suporte': '#64748b'
      };
      return defaultPalette[column] || '#94a3b8';
    },
    async saveColumnColor(column, color) {
      let config = { description: '', colors: {} };
      if (this.kanbanAttribute && this.kanbanAttribute.attributeDescription) {
        try {
          const parsed = JSON.parse(this.kanbanAttribute.attributeDescription);
          if (parsed && typeof parsed === 'object') {
            config = { ...config, ...parsed };
          } else {
            config.description = this.kanbanAttribute.attributeDescription;
          }
        } catch (e) {
          config.description = this.kanbanAttribute.attributeDescription;
        }
      }
      if (!config.colors) config.colors = {};
      config.colors[column] = color;
      const newDescription = JSON.stringify(config);
      
      try {
        await this.$store.dispatch('attributes/update', {
          id: this.kanbanAttribute.id,
          attribute_description: newDescription
        });
      } catch (e) {
        // error
      }
    },
    async onCardChange(evt, newColumn) {
      if (evt.added) {
        const conversation = evt.added.element;
        const convDisplayId = conversation.display_id || conversation.id;
        const customAttrs = this.getCustomAttributes(conversation);
        
        customAttrs[this.attributeKey] = newColumn;
        conversation.custom_attributes = customAttrs;
        
        const globalIdx = this.conversations.findIndex(c => (c.display_id || c.id) === convDisplayId);
        if (globalIdx !== -1) {
          this.conversations[globalIdx].custom_attributes = customAttrs;
        }
        
        if (window.__kanban_cache) {
          window.__kanban_cache[this.attributeKey] = this.conversations;
        }

        try {
          await window.axios.post(
            `/api/v1/accounts/${this.accountId}/conversations/${convDisplayId}/custom_attributes`,
            {
              custom_attributes: {
                ...customAttrs,
                [this.attributeKey]: newColumn
              }
            }
          );
        } catch (error) {
          console.error('Erro ao sincronizar movimentação do card', error);
        }
      }
    },
    async openConversation(conversation) {
      const convDisplayId = conversation.display_id || conversation.id;
      this.selectedConversation = conversation;
      this.showConversationModal = true;
      
      if (this.updateUISettings) {
        this.updateUISettings({ is_contact_sidebar_open: true });
      }
      
      try {
        const fullConversation = await this.$store.dispatch('getConversation', convDisplayId);
        const activeData = fullConversation || conversation;
        this.selectedConversation = activeData;
        await this.$store.dispatch('setActiveChat', { data: activeData });
      } catch (error) {
        console.error('Erro ao carregar conversa no Modal', error);
      }
    },
    toggleContactSidebar() {
      if (this.updateUISettings && this.uiSettings) {
        this.updateUISettings({
          is_contact_sidebar_open: !this.isSidebarOpen,
        });
      }
    },
    navigateToFullConversation() {
      if (this.selectedConversation) {
        const convDisplayId = this.selectedConversation.display_id || this.selectedConversation.id;
        this.closeConversationModal();
        this.$router.push({
          name: 'inbox_conversation',
          params: {
            accountId: this.accountId,
            conversation_id: convDisplayId,
          },
        });
      }
    },
    closeConversationModal() {
      this.showConversationModal = false;
      this.selectedConversation = null;
      this.$store.dispatch('clearSelectedState');
    }
  }
};
</script>

<style scoped>
:deep(.modal-container.kanban-modal) {
  width: 94vw !important;
  max-width: 1440px !important;
  border-radius: 0.85rem !important;
  overflow: hidden;
  padding: 0 !important;
}

.kanban-columns-wrapper {
  scrollbar-width: thin;
}
</style>
